import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../styles/MainContainer.css';
import './ParticipantGroups.css';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import ActionButton from '../input/ActionButton';
import OfflineBanner from '../shell/OfflineBanner';
import { usePopup } from '../context/PopupContext';
import { useOffline } from '../context/OfflineContext';
import useParticipantGroups, { setParticipantGroups } from '../../hooks/useParticipantGroups';
import useRoleSettings, { reloadRoleSettings, setRoleSettings } from '../../hooks/useRoleSettings';
import { cachedGet, REFERENCE_URLS } from '../../offline/offlineApi';
import * as db from '../../offline/db';
import {
  normalizeParticipantGroup, normalizeParticipantGroups, normalizeRoleSetting,
} from '../../util/participantGroups';
import ParticipantGroupList from '../participant-group/admin/ParticipantGroupList';
import ParticipantGroupEditor from '../participant-group/admin/ParticipantGroupEditor';
import RoleSettingList from '../participant-group/admin/RoleSettingList';
import RoleSettingEditor from '../participant-group/admin/RoleSettingEditor';
import { apiErrorMessage } from '../participant-group/admin/groupAdminUtils';

/**
 * Admin page /groups ("Gruppen", quick-access tile on the home page): the guest group pinned on top,
 * the other groups in display order (reorder via up/down), editor with tabs next to the list (phone: the
 * editor replaces the list). The list comes from useParticipantGroups (one shared, offline-cached fetch)
 * and is fetched once more per visit, because member counts and "in use" change elsewhere (player profile).
 * Mutations patch the shared list from their response (the full group) — GET
 * /ParticipantGroup/GetAll?includeArchived=true runs only after mutations without one (delete, end all) —
 * so dots, chips and pickers of the whole app update.
 * Below the groups: section "Rollen" (Orga/Admin settings from useRoleSettings — they change only here,
 * so no refetch per visit); a saved role patches the shared settings from the PUT response.
 */
function ParticipantGroups() {
  const { groups, active, guest, loaded, failed } = useParticipantGroups();
  const { roles, byRole, loaded: rolesLoaded, failed: rolesFailed } = useRoleSettings();
  const { isOffline } = useOffline();
  const { openPopup } = usePopup();

  // selection: null | { mode: 'edit', id } | { mode: 'create' } | { mode: 'role', role }
  const [selection, setSelection] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [rolesReloading, setRolesReloading] = useState(false);
  const [error, setError] = useState({ title: '', message: '' });
  const [pendingSelection, setPendingSelection] = useState(undefined);

  // Unsaved editor changes (guard before switching groups); a ref, so typing never re-renders the page.
  const dirtyRef = useRef(false);
  const handleDirtyChange = useCallback((dirty) => { dirtyRef.current = dirty; }, []);

  // Loaded once per page visit and shared by all editor instances.
  const mailRulesCache = useRef(new Map());
  const [textblocks, setTextblocks] = useState({ status: 'idle', list: [] });
  const textblocksRequested = useRef(false);

  const ensureTextblocks = useCallback(() => {
    if (textblocksRequested.current) return;
    textblocksRequested.current = true;
    setTextblocks(prev => ({ ...prev, status: 'loading' }));
    api.get('/Textblock/GetAllNames?language=DE')
      // System blocks (2FA, newsletter confirmations, orga welcome) are never assignable to mail rules.
      .then(res => setTextblocks({ status: 'loaded', list: (Array.isArray(res.data) ? res.data : []).filter(tb => !tb.isSystemBlock) }))
      .catch(() => {
        textblocksRequested.current = false;
        setTextblocks({ status: 'error', list: [] });
      });
  }, []);

  // One GET → shared list (returns the normalized list, null on failure).
  const refreshGroups = useCallback(async () => {
    try {
      const data = await cachedGet(REFERENCE_URLS.participantGroups);
      setParticipantGroups(data);
      return normalizeParticipantGroups(data);
    } catch {
      return null;
    }
  }, []);

  // Replaces the shared list from a response and keeps the offline copy in line, like a GET through
  // cachedGet would (best effort).
  const publishGroups = useCallback((list) => {
    setParticipantGroups(list);
    if (db.isSupported()) db.putRead(REFERENCE_URLS.participantGroups, list).catch(() => {});
  }, []);

  // Update/Archive/Unarchive/Create answer with the full group (incl. member count and "in use") → patch
  // it into the shared list instead of fetching GetAll again. Returns the normalized group, null for a
  // response without one (the caller then refetches).
  const groupsRef = useRef(groups);
  groupsRef.current = groups;
  const applyGroup = useCallback((rawGroup) => {
    const updated = normalizeParticipantGroup(rawGroup);
    if (!updated) return null;
    const list = groupsRef.current;
    publishGroups(list.some(g => g.id === updated.id)
      ? list.map(g => (g.id === updated.id ? updated : g))
      : [...list, updated]);
    return updated;
  }, [publishGroups]);

  // The shared list is loaded once per page load, but member counts and "in use" change on the player
  // profile (memberships, roles) → fetch it once more per visit when it was already loaded before (not
  // loaded yet = the hook fetches it right now). Only on mount: "Erneut verbinden" reloads it anyway.
  const loadedOnMount = useRef(loaded);
  const refreshedOnMount = useRef(false);
  useEffect(() => {
    if (refreshedOnMount.current) return;
    refreshedOnMount.current = true;
    if (loadedOnMount.current && !isOffline) refreshGroups();
  }, [isOffline, refreshGroups]);

  const reload = useCallback(() => {
    setReloading(true);
    refreshGroups().finally(() => setReloading(false));
  }, [refreshGroups]);

  const reloadRoles = useCallback(() => {
    setRolesReloading(true);
    reloadRoleSettings().finally(() => setRolesReloading(false));
  }, []);

  // PUT /Role/UpdateSettings answers with the saved setting → patch it into the shared settings (names and
  // colors of every dot/label update) and the offline copy. Returns the normalized setting; a response
  // without one reloads the settings instead (→ null).
  const rolesRef = useRef(roles);
  rolesRef.current = roles;
  const applyRoleSetting = useCallback((rawSetting) => {
    const updated = normalizeRoleSetting(rawSetting);
    if (!updated) {
      reloadRoleSettings();
      return null;
    }
    const list = rolesRef.current.some(r => r.role === updated.role)
      ? rolesRef.current.map(r => (r.role === updated.role ? updated : r))
      : [...rolesRef.current, updated];
    setRoleSettings(list);
    if (db.isSupported()) db.putRead(REFERENCE_URLS.roleSettings, list).catch(() => {});
    return updated;
  }, []);

  // After "Erneut verbinden" the shared list reloads itself (OfflineContext → reloadParticipantGroups).

  const showError = useCallback((title, message) => {
    setError({ title, message });
    openPopup('groupactionerror', title);
  }, [openPopup]);

  const selectedId = selection?.mode === 'edit' ? selection.id : null;
  const selectedGroup = selectedId != null ? groups.find(g => g.id === selectedId) ?? null : null;
  const selectedRole = selection?.mode === 'role' ? selection.role : null;
  const selectedRoleSetting = selectedRole ? byRole.get(selectedRole) ?? null : null;

  const others = useMemo(() => groups.filter(g => !g.isGuest), [groups]);
  const archivedCount = others.filter(g => g.isArchived).length;
  // The open group keeps its row even when archived groups are hidden.
  const visibleOthers = others.filter(g => showArchived || !g.isArchived || g.id === selectedId);

  // Deleted meanwhile (or never existed once the list is loaded) → back to the list.
  useEffect(() => {
    if ((selection?.mode === 'edit' && loaded && !selectedGroup)
      || (selection?.mode === 'role' && rolesLoaded && !selectedRoleSetting)) {
      dirtyRef.current = false;
      setSelection(null);
    }
  }, [selection, loaded, selectedGroup, rolesLoaded, selectedRoleSetting]);

  const applySelection = useCallback((next) => {
    dirtyRef.current = false;
    setSelection(next);
    // Phone (same 1000px breakpoint as the CSS): the editor replaces the list → start at the top.
    if (window.innerWidth < 1000) window.scrollTo({ top: 0 });
  }, []);

  // Switching with unsaved changes asks first. `next` = null (back to list) | selection object.
  const requestSelection = useCallback((next) => {
    if (dirtyRef.current) {
      setPendingSelection(next);
      openPopup('discardgroupchanges', 'discard');
      return;
    }
    applySelection(next);
  }, [applySelection, openPopup]);

  const handleSelect = (id) => {
    if (selectedId === id) return;
    requestSelection({ mode: 'edit', id });
  };

  const handleSelectRole = (role) => {
    if (selectedRole === role) return;
    requestSelection({ mode: 'role', role });
  };

  const handleConfirmDiscard = () => {
    if (pendingSelection !== undefined) applySelection(pendingSelection);
    setPendingSelection(undefined);
  };

  // Up/down among the visible rows; the full non-guest order (incl. hidden archived groups) is sent.
  const handleMove = (id, direction) => {
    if (reordering || isOffline) return;
    const index = visibleOthers.findIndex(g => g.id === id);
    const neighbor = visibleOthers[index + direction];
    if (index < 0 || !neighbor) return;

    const ids = others.map(g => g.id);
    const a = ids.indexOf(id);
    const b = ids.indexOf(neighbor.id);
    [ids[a], ids[b]] = [ids[b], ids[a]];

    // Optimistic: reorder the shared list right away, the response (full list) replaces it.
    const previous = groups;
    const sortOf = new Map(ids.map((gid, i) => [gid, (i + 1) * 10]));
    setParticipantGroups(groups.map(g => (sortOf.has(g.id) ? { ...g, sortOrder: sortOf.get(g.id) } : g)));

    setReordering(true);
    api.put('/ParticipantGroup/Reorder', { participantGroupIds: ids })
      .then(res => {
        if (Array.isArray(res.data)) publishGroups(res.data);
        else refreshGroups();
      })
      .catch(err => {
        setParticipantGroups(previous);
        showError('Sortieren fehlgeschlagen', apiErrorMessage(err, 'Die Reihenfolge konnte nicht gespeichert werden.'));
      })
      .finally(() => setReordering(false));
  };

  const handleCreated = useCallback((id) => {
    applySelection(id != null ? { mode: 'edit', id } : null);
  }, [applySelection]);

  // An archived group stays open in the editor → make sure its row stays visible as well.
  const handleArchived = useCallback(() => setShowArchived(true), []);

  const handleDeleted = useCallback(() => applySelection(null), [applySelection]);

  const handleBack = useCallback(() => requestSelection(null), [requestSelection]);

  const subtitle = !loaded
    ? (failed ? '' : 'Lädt …')
    : `${active.length} ${active.length === 1 ? 'aktive Gruppe' : 'aktive Gruppen'}`
      + (archivedCount > 0 ? ` · ${archivedCount} archiviert` : '');

  let listContent;
  if (!loaded) {
    if (isOffline) {
      listContent = <p className="groupsPageMessage">Offline nicht verfügbar.</p>;
    } else if (failed) {
      listContent = (
        <div className="groupsPageMessage">
          <p>Die Gruppen konnten nicht geladen werden.</p>
          <ActionButton icon="refresh" label="Erneut laden" onClick={reload} disabled={reloading} />
        </div>
      );
    } else {
      listContent = null;
    }
  } else {
    listContent = (
      <ParticipantGroupList
        guest={guest}
        groups={visibleOthers}
        selectedId={selectedId}
        onSelect={handleSelect}
        onMove={handleMove}
        reorderDisabled={isOffline || reordering}
        reorderDisabledReason={isOffline ? 'Offline nicht verfügbar' : ''}
        archivedCount={archivedCount}
        showArchived={showArchived}
        onToggleArchived={setShowArchived}
      >
        <RoleSettingList
          roles={roles}
          loaded={rolesLoaded}
          failed={rolesFailed}
          selectedRole={selectedRole}
          onSelect={handleSelectRole}
          onRetry={reloadRoles}
          retrying={rolesReloading}
          isOffline={isOffline}
        />
      </ParticipantGroupList>
    );
  }

  const hasEditor = selection?.mode === 'create' || !!selectedGroup || !!selectedRoleSetting;
  let editorKey = `group-${selection?.id}`;
  if (selection?.mode === 'create') editorKey = 'new';
  else if (selection?.mode === 'role') editorKey = `role-${selection.role}`;

  let editor = null;
  if (selectedRoleSetting) {
    editor = (
      <RoleSettingEditor
        key={editorKey}
        setting={selectedRoleSetting}
        roles={roles}
        groups={groups}
        isOffline={isOffline}
        onBack={handleBack}
        onDirtyChange={handleDirtyChange}
        onSaved={applyRoleSetting}
      />
    );
  } else if (hasEditor) {
    editor = (
      <ParticipantGroupEditor
        key={editorKey}
        group={selectedGroup}
        groups={groups}
        guest={guest}
        isOffline={isOffline}
        refreshGroups={refreshGroups}
        applyGroup={applyGroup}
        showError={showError}
        onBack={handleBack}
        onCreated={handleCreated}
        onArchived={handleArchived}
        onDeleted={handleDeleted}
        onDirtyChange={handleDirtyChange}
        mailRulesCache={mailRulesCache.current}
        textblocks={textblocks}
        ensureTextblocks={ensureTextblocks}
      />
    );
  }

  return (
    <React.Fragment>
      <main className={`defaultExtendedMain groupsPageMain${hasEditor ? ' hasSelection' : ''}`}>
        <OfflineBanner />
        <div className="groupsPageHeader">
          <div className="groupsPageHeaderText">
            <h2 className="groupsPageTitle">Gruppen</h2>
            {subtitle && <span className="groupsPageSubtitle">{subtitle}</span>}
          </div>
          <ActionButton
            icon="group_add"
            label="Neue Gruppe"
            type={3}
            onClick={() => requestSelection({ mode: 'create' })}
            disabled={isOffline || !loaded || selection?.mode === 'create'}
          />
        </div>

        <div className={`groupsPageBody${hasEditor ? ' hasSelection' : ''}`}>
          <div className="groupsPageListPane">{listContent}</div>
          <div className="groupsPageEditorPane">
            {editor ?? (loaded && (
              <div className="groupsPageEmpty">
                <span className="material-symbols-rounded groupsPageEmptyIcon" aria-hidden="true">groups</span>
                <p>
                  Wähle eine Gruppe aus, um Preise, Anmeldeweg und Mails einzustellen, oder eine Rolle für Farbe,
                  Spieltagleitung und Lohn – neue Gruppen legst du über „Neue Gruppe“ an.
                </p>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Popup
        name="discardgroupchanges"
        headerText="Änderungen verwerfen?"
        confirmText="Verwerfen"
        type="warning"
        onConfirm={handleConfirmDiscard}
        withCancel
      >
        <p>
          {selection?.mode === 'role' ? 'Die Rolle' : 'Die Gruppe'} hat ungespeicherte Änderungen. Sollen sie
          verworfen werden?
        </p>
      </Popup>
      <Popup name="groupactionerror" headerText={error.title || 'Fehler'} confirmText="Ok" type="error">
        <p>{error.message}</p>
      </Popup>
    </React.Fragment>
  );
}

export default ParticipantGroups;
